import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { mapAuth0RoleToInternal } from '../config/auth0';
import { useGetUsersQuery } from '../services/api';

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useAuth0();
  const [currentUser, setCurrentUser] = useState(null);
  // Fetch users from API to get accurate role from Management API
  const { data: usersData } = useGetUsersQuery(undefined, { skip: !isAuthenticated });

  useEffect(() => {
    if (isAuthenticated && user) {
      // The logged-in user IS currentUser. Set immediately from Auth0, refine when API returns.
      const currentUserId = user.sub;
      const apiUser = usersData?.data?.users?.find(u => u.id === currentUserId);
      
      let auth0Role = null;
      if (usersData !== undefined) {
        if (apiUser?.role) auth0Role = apiUser.role;
        if (!auth0Role) auth0Role = user.app_metadata?.roles?.[0];
        if (!auth0Role) auth0Role = user['https://krossglobal.com/roles']?.[0];
        if (!auth0Role) {
          console.warn('⚠️ No role found in Auth0 metadata - defaulting to viewer');
          auth0Role = 'viewer';
        }
      } else {
        // API not loaded yet: use token/claim or default viewer so currentUser is still set
        auth0Role = user.app_metadata?.roles?.[0] || user['https://krossglobal.com/roles']?.[0] || 'viewer';
      }
      
      const internalRole = mapAuth0RoleToInternal(auth0Role);
      
      let firstName = apiUser?.firstName || '';
      let lastName = apiUser?.lastName || '';
      if (!firstName || !lastName) {
        const nameParts = (user.name || user.nickname || 'User').split(' ');
        firstName = firstName || nameParts[0] || '';
        lastName = lastName || nameParts.slice(1).join(' ') || '';
      }
      
      const profileUpdatedAt = apiUser?.profileUpdatedAt || user.app_metadata?.profile_updated_at || null;
      const phone = apiUser?.phone || user.phone_number || '';
      const title = apiUser?.title || user.user_metadata?.title || '';
      const department = apiUser?.department || user.user_metadata?.department || '';
      const location = apiUser?.location || user.user_metadata?.location || '';
      const timezone = apiUser?.timezone || user.user_metadata?.timezone || 'America/Los_Angeles';
      const bio = apiUser?.bio || user.user_metadata?.bio || '';
      const lastLogin = apiUser?.lastLogin || user.last_login || null;
      const createdAt = apiUser?.createdAt || user.created_at || new Date().toISOString();
      const passwordLastChanged = apiUser?.passwordLastChanged || null;
      // Avatar: API (tenant profile) first, then Auth0 picture so the logged-in user always has a face
      const avatar = apiUser?.avatar || user.user_metadata?.avatar || user.picture || null;
      
      setCurrentUser({
        id: user.sub,
        firstName,
        lastName,
        name: user.name || user.nickname || 'User',
        email: user.email,
        phone,
        title,
        department,
        location,
        timezone,
        bio,
        avatar,
        role: internalRole,
        status: 'active',
        lastLogin,
        createdAt,
        profileUpdatedAt,
        passwordLastChanged,
        auth0User: user
      });
    } else {
      setCurrentUser(null);
    }
  }, [isAuthenticated, user, usersData]);

  const updateUser = (updates) => {
    setCurrentUser(prev => ({
      ...prev,
      ...updates,
      // Ensure name is always synced with firstName and lastName
      name: updates.firstName && updates.lastName 
        ? `${updates.firstName} ${updates.lastName}`
        : prev?.name
    }));
  };


  const value = {
    currentUser,
    updateUser,
    setCurrentUser,
    isAuthenticated,
    isLoading
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;
